import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import workflowService from '../../../services/workflowService';

const WorkflowList = ({ workflows, onEdit, onDelete, onCreateNew }) => {
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const handleDelete = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) {
      return;
    }

    setDeletingId(workflowId);
    setError(null);

    try {
      const { error: deleteError } = await workflowService?.deleteWorkflow(workflowId);
      
      if (deleteError) {
        setError(deleteError);
        return;
      }
      
      onDelete?.();
    } catch (err) {
      setError('Failed to delete workflow');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (workflow) => {
    const newStatus = workflow?.status === 'active' ? 'paused' : 'active';
    
    try {
      await workflowService?.updateWorkflow(workflow?.id, { status: newStatus });
      onDelete?.(); // Refresh the list
    } catch (err) {
      setError('Failed to update workflow status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'paused': return 'text-warning';
      case 'completed': return 'text-primary';
      case 'draft': return 'text-muted-foreground';
      case 'archived': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'paused': return 'Pause';
      case 'completed': return 'CheckCircle';
      case 'draft': return 'FileText';
      case 'archived': return 'Archive';
      default: return 'Circle';
    }
  };

  const getTriggerTypeLabel = (triggerType) => {
    switch (triggerType) {
      case 'manual': return 'Manual';
      case 'time_based': return 'Time-based';
      case 'event_based': return 'Event-based';
      case 'conditional': return 'Conditional';
      default: return 'Unknown';
    }
  };

  if (workflows?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="text-center py-16 px-6">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-5">
            <Icon name="Zap" size={36} className="text-muted-foreground opacity-60" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            No workflows yet
          </h3>
          <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
            Create automated workflows to streamline your lead generation and follow-up process.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={onCreateNew}
              iconName="Plus"
              iconPosition="left"
            >
              Create Your First Workflow
            </Button>
            <Button
              variant="outline"
              iconName="BookOpen"
              iconPosition="left"
              onClick={() => window.open('/help-support', '_self')}
            >
              View Templates
            </Button>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Workflows automate emails, follow-ups, and lead scoring based on triggers you define.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-destructive text-sm">{error}</span>
          </div>
        </div>
      )}
      <div className="grid gap-6">
        {workflows?.map(workflow => (
          <div key={workflow?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">
                    {workflow?.name}
                  </h3>
                  <div className={`flex items-center space-x-1 ${getStatusColor(workflow?.status)}`}>
                    <Icon name={getStatusIcon(workflow?.status)} size={16} />
                    <span className="text-sm font-medium capitalize">
                      {workflow?.status}
                    </span>
                  </div>
                </div>
                
                {workflow?.description && (
                  <p className="text-muted-foreground text-sm mb-4">
                    {workflow?.description}
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Settings" size={16} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Trigger</p>
                      <p className="text-sm font-medium text-foreground">
                        {getTriggerTypeLabel(workflow?.trigger_type)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="GitBranch" size={16} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Steps</p>
                      <p className="text-sm font-medium text-foreground">
                        {workflow?.workflow_steps?.length || 0}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="Play" size={16} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Executions</p>
                      <p className="text-sm font-medium text-foreground">
                        {workflow?.execution_count || 0}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Success Rate</p>
                      <p className="text-sm font-medium text-foreground">
                        {workflow?.execution_count > 0 
                          ? Math.round((workflow?.success_count / workflow?.execution_count) * 100)
                          : 0
                        }%
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span>
                    Created {new Date(workflow?.created_at)?.toLocaleDateString()}
                  </span>
                  {workflow?.updated_at !== workflow?.created_at && (
                    <span>
                      Updated {new Date(workflow?.updated_at)?.toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleStatus(workflow)}
                  iconName={workflow?.status === 'active' ? 'Pause' : 'Play'}
                  disabled={workflow?.status === 'draft'}
                  title={workflow?.status === 'active' ? 'Pause workflow' : 'Activate workflow'}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit?.(workflow)}
                  iconName="Edit2"
                  title="Edit workflow"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(workflow?.id)}
                  iconName={deletingId === workflow?.id ? "Loader2" : "Trash2"}
                  className={`text-destructive hover:text-destructive ${deletingId === workflow?.id ? 'animate-spin' : ''}`}
                  disabled={deletingId === workflow?.id}
                  title="Delete workflow"
                />
              </div>
            </div>

            {/* Workflow Steps Preview */}
            {workflow?.workflow_steps?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm font-medium text-foreground mb-2">Workflow Steps:</p>
                <div className="flex flex-wrap gap-2">
                  {workflow?.workflow_steps
                    ?.sort((a, b) => a?.step_order - b?.step_order)
                    ?.slice(0, 5)
                    ?.map((step, index) => (
                      <div
                        key={step?.id || index}
                        className="flex items-center space-x-1 bg-muted px-2 py-1 rounded text-xs"
                      >
                        <span className="font-medium text-foreground">{index + 1}.</span>
                        <span className="text-muted-foreground">{step?.name}</span>
                      </div>
                    ))
                  }
                  {workflow?.workflow_steps?.length > 5 && (
                    <div className="flex items-center px-2 py-1 text-xs text-muted-foreground">
                      +{workflow?.workflow_steps?.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowList;